/**
 * projectSerializer.js
 *
 * Save/Load a DAW project as a .dawproject zip file.
 *
 * ZIP layout:
 *   project.json          — all metadata, tracks, MIDI clips, automation
 *   audio/<clipId>.wav    — one WAV per audio clip (raw PCM, 32-bit float)
 */

import JSZip from 'jszip';
import { analyzeAudioBuffer } from './audioUtils';

// ─────────────────────────────────────────────
// WAV encoding helpers (32-bit float PCM, no compression)
// ─────────────────────────────────────────────

function writeString(view, offset, str) {
  for (let i = 0; i < str.length; i++) {
    view.setUint8(offset + i, str.charCodeAt(i));
  }
}

/**
 * Encode an AudioBuffer as a WAV ArrayBuffer (IEEE 32-bit float PCM).
 */
export function audioBufferToWav(buffer) {
  const numChannels = buffer.numberOfChannels;
  const sampleRate = buffer.sampleRate;
  const numFrames = buffer.length;
  const bytesPerSample = 4; // 32-bit float
  const blockAlign = numChannels * bytesPerSample;
  const byteRate = sampleRate * blockAlign;
  const dataSize = numFrames * blockAlign;
  const headerSize = 44;

  const arrayBuffer = new ArrayBuffer(headerSize + dataSize);
  const view = new DataView(arrayBuffer);

  // RIFF chunk descriptor
  writeString(view, 0, 'RIFF');
  view.setUint32(4, 36 + dataSize, true);
  writeString(view, 8, 'WAVE');

  // fmt sub-chunk
  writeString(view, 12, 'fmt ');
  view.setUint32(16, 16, true);          // sub-chunk size
  view.setUint16(20, 3, true);           // audio format: IEEE float = 3
  view.setUint16(22, numChannels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, byteRate, true);
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, 32, true);          // bits per sample

  // data sub-chunk
  writeString(view, 36, 'data');
  view.setUint32(40, dataSize, true);

  // Interleave channel data
  let offset = 44;
  for (let i = 0; i < numFrames; i++) {
    for (let ch = 0; ch < numChannels; ch++) {
      const sample = buffer.getChannelData(ch)[i];
      view.setFloat32(offset, sample, true);
      offset += 4;
    }
  }

  return arrayBuffer;
}

// ─────────────────────────────────────────────
// Serialise a single clip to JSON-safe form
// ─────────────────────────────────────────────

function serialiseClip(clip) {
  const base = {
    id: clip.id,
    name: clip.name,
    type: clip.type,
    start: clip.start,
    duration: clip.duration,
    sourceDuration: clip.sourceDuration ?? clip.duration,
    loop: clip.loop ?? false,
    gain: clip.gain ?? 1,
    playbackRate: clip.playbackRate ?? 1,
    fadeIn: clip.fadeIn ?? 0,
    fadeOut: clip.fadeOut ?? 0,
    trimStart: clip.trimStart ?? 0,
    trimEnd: clip.trimEnd ?? 0,
    contentOffset: clip.contentOffset ?? 0,
  };

  if (clip.type === 'audio') {
    base.audioLength = clip.audioLength ?? 0;
    // audioBuffer is serialised separately as a WAV file; just record the id reference
    base.audioFileRef = `audio/${clip.id}.wav`;
    // waveform is re-computed on load — don't need to store it (saves space)
  }

  if (clip.type === 'midi') {
    base.notes = (clip.notes || []).map(n => ({ ...n }));
  }

  return base;
}

// ─────────────────────────────────────────────
// Serialise a full track
// ─────────────────────────────────────────────

function serialiseTrack(track) {
  return {
    id: track.id,
    name: track.name,
    type: track.type,
    color: track.color,
    instrument: track.instrument,
    soundfontInstrument: track.soundfontInstrument ?? null,
    volume: track.volume ?? 1,
    pan: track.pan ?? 0,
    mute: track.mute ?? false,
    solo: track.solo ?? false,
    reverbSend: track.reverbSend ?? 0,
    drumKeyMap: track.drumKeyMap
      ? Object.fromEntries(
        track.drumKeyMap instanceof Map
          ? track.drumKeyMap
          : Object.entries(track.drumKeyMap)
      )
      : {},
    pianoKeyMapCustom: track.pianoKeyMapCustom
      ? Object.fromEntries(
        track.pianoKeyMapCustom instanceof Map
          ? track.pianoKeyMapCustom
          : Object.entries(track.pianoKeyMapCustom)
      )
      : null,
    clips: (track.clips || []).map(serialiseClip),
  };
}

// ─────────────────────────────────────────────
// PUBLIC: Save project → .dawproject zip download
// ─────────────────────────────────────────────

/**
 * @param {Object}   projectState
 * @param {string}   projectState.projectName
 * @param {number}   projectState.bpm
 * @param {Object}   projectState.timeSignature  { numerator, denominator }
 * @param {number}   projectState.masterVolume
 * @param {boolean}  projectState.snapToGrid
 * @param {number}   projectState.gridSize
 * @param {number}   projectState.pixelsPerBeat
 * @param {number}   projectState.currentTime    playhead beat position
 * @param {number|null} projectState.loopStart
 * @param {number|null} projectState.loopEnd
 * @param {boolean}  projectState.loopEnabled
 * @param {boolean}  projectState.metronomeEnabled
 * @param {boolean}  projectState.showAutomation
 * @param {Array}    projectState.tracks
 * @param {Object}   projectState.automationPoints   { [trackId]: { volume, pan } }
 * @param {Object}   projectState.automationWriteMode { [trackId]: 'read'|'write' }
 * @param {Object}   projectState.automationParam     { [trackId]: 'volume'|'pan' }
 * @param {Array}    projectState.projectLyrics       { id, startBeat, text }[]
 * @param {boolean}  projectState.showLyricsRow
 * @param {boolean}  projectState.lyricsRowEditMode   lyrics row: edit vs view-only
 */
export async function saveProject(projectState) {
  const {
    projectName = 'Untitled Project',
    bpm,
    timeSignature,
    masterVolume,
    snapToGrid,
    gridSize,
    pixelsPerBeat,
    currentTime,
    loopStart,
    loopEnd,
    loopEnabled,
    metronomeEnabled,
    showAutomation,
    tracks,
    automationPoints,
    automationWriteMode,
    automationParam,
    projectLyrics,
    showLyricsRow,
    lyricsRowEditMode,
  } = projectState;

  const zip = new JSZip();

  // Build project.json
  const serialisedTracks = tracks.map(serialiseTrack);

  const projectJson = {
    version: '1.0',
    savedAt: new Date().toISOString(),
    projectName,
    bpm,
    timeSignature,
    masterVolume,
    snapToGrid,
    gridSize,
    pixelsPerBeat,
    currentTime,
    loopStart,
    loopEnd,
    loopEnabled,
    metronomeEnabled,
    showAutomation,
    automationPoints,
    automationWriteMode,
    automationParam,
    projectLyrics: projectLyrics ?? [],
    showLyricsRow: showLyricsRow ?? false,
    lyricsRowEditMode: lyricsRowEditMode ?? true,
    tracks: serialisedTracks,
  };

  zip.file('project.json', JSON.stringify(projectJson, null, 2));

  // Encode each audio clip's AudioBuffer as WAV and add to zip
  const audioFolder = zip.folder('audio');
  for (const track of tracks) {
    for (const clip of track.clips || []) {
      if (clip.type === 'audio' && clip.audioBuffer) {
        try {
          const wavArrayBuffer = audioBufferToWav(clip.audioBuffer);
          audioFolder.file(`${clip.id}.wav`, wavArrayBuffer);
        } catch (err) {
          console.warn(`[Save] Could not encode audio for clip ${clip.id}:`, err);
        }
      }
    }
  }

  // Generate and trigger download
  const blob = await zip.generateAsync({
    type: 'blob',
    compression: 'DEFLATE',
    compressionOptions: { level: 3 }, // fast compression; audio is already compressed-ish
  });

  const safeName = projectName.replace(/[^a-z0-9_\- ]/gi, '_').trim() || 'project';
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${safeName}.ibom`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 10000);
}

// ─────────────────────────────────────────────
// Deserialise a clip back to runtime form
// ─────────────────────────────────────────────

async function deserialiseClip(clipData, audioFolder, audioCtx) {
  const clip = { ...clipData };

  if (clip.type === 'audio' && clip.audioFileRef) {
    const fileName = `${clip.id}.wav`;
    const wavFile = audioFolder ? audioFolder.file(fileName) : null;

    if (wavFile) {
      try {
        const arrayBuffer = await wavFile.async('arraybuffer');
        const audioBuffer = await audioCtx.decodeAudioData(arrayBuffer);
        clip.audioBuffer = audioBuffer;
        clip.waveform = analyzeAudioBuffer(audioBuffer);
      } catch (err) {
        console.warn(`[Load] Could not decode audio for clip ${clip.id}:`, err);
        clip.audioBuffer = null;
        clip.waveform = null;
      }
    } else {
      console.warn(`[Load] Audio file not found in zip: ${fileName}`);
      clip.audioBuffer = null;
      clip.waveform = null;
    }

    delete clip.audioFileRef;
  }

  // Ensure arrays / defaults exist
  clip.notes = clip.notes || [];
  clip.sourceDuration = clip.sourceDuration ?? clip.duration;
  clip.loop = clip.loop ?? false;
  clip.fadeIn = clip.fadeIn ?? 0;
  clip.fadeOut = clip.fadeOut ?? 0;
  clip.trimStart = clip.trimStart ?? 0;
  clip.trimEnd = clip.trimEnd ?? 0;
  clip.contentOffset = clip.contentOffset ?? 0;
  clip.gain = clip.gain ?? 1;
  clip.playbackRate = clip.playbackRate ?? 1;

  return clip;
}

// ─────────────────────────────────────────────
// Deserialise a track back to runtime form
// ─────────────────────────────────────────────

async function deserialiseTrack(trackData, audioFolder, audioCtx) {
  const clips = await Promise.all(
    (trackData.clips || []).map(c => deserialiseClip(c, audioFolder, audioCtx))
  );

  return {
    ...trackData,
    clips,
    // Restore Map-like objects (stored as plain objects in JSON)
    drumKeyMap: trackData.drumKeyMap || {},
    pianoKeyMapCustom: trackData.pianoKeyMapCustom || null,
    // Defaults
    volume: trackData.volume ?? 1,
    pan: trackData.pan ?? 0,
    mute: trackData.mute ?? false,
    solo: trackData.solo ?? false,
    reverbSend: trackData.reverbSend ?? 0,
  };
}

// ─────────────────────────────────────────────
// PUBLIC: Load project from .dawproject File → state object
// ─────────────────────────────────────────────

/**
 * Reads a .dawproject zip file and returns a plain object containing
 * all the state fields that App.jsx should restore.
 *
 * @param {File}         file          — The file picked by the user
 * @param {AudioContext} audioCtx      — Web Audio context for decoding audio
 * @returns {Promise<Object>}          — Resolved project state
 */
export async function loadProject(file, audioCtx) {
  const arrayBuffer = await file.arrayBuffer();
  const zip = await JSZip.loadAsync(arrayBuffer);

  // Parse project.json
  const jsonFile = zip.file('project.json');
  if (!jsonFile) throw new Error('Invalid .dawproject file: missing project.json');
  const jsonText = await jsonFile.async('string');
  const projectJson = JSON.parse(jsonText);

  if (!projectJson.version) throw new Error('Unrecognised project file format.');

  const audioFolder = zip.folder('audio');

  // Deserialise tracks (this re-decodes all WAV files in parallel per track)
  const tracks = await Promise.all(
    (projectJson.tracks || []).map(t => deserialiseTrack(t, audioFolder, audioCtx))
  );

  return {
    projectName: projectJson.projectName ?? 'Untitled Project',
    bpm: projectJson.bpm ?? 120,
    timeSignature: projectJson.timeSignature ?? { numerator: 4, denominator: 4 },
    masterVolume: projectJson.masterVolume ?? 0.8,
    snapToGrid: projectJson.snapToGrid ?? true,
    gridSize: projectJson.gridSize ?? 16,
    pixelsPerBeat: projectJson.pixelsPerBeat ?? 50,
    currentTime: projectJson.currentTime ?? 0,
    loopStart: projectJson.loopStart ?? null,
    loopEnd: projectJson.loopEnd ?? null,
    loopEnabled: projectJson.loopEnabled ?? false,
    metronomeEnabled: projectJson.metronomeEnabled ?? false,
    showAutomation: projectJson.showAutomation ?? false,
    automationPoints: projectJson.automationPoints ?? {},
    automationWriteMode: projectJson.automationWriteMode ?? {},
    automationParam: projectJson.automationParam ?? {},
    projectLyrics: projectJson.projectLyrics ?? [],
    showLyricsRow: projectJson.showLyricsRow ?? false,
    lyricsRowEditMode: projectJson.lyricsRowEditMode ?? true,
    tracks,
  };
}
